// src/pages/admin/BranchList.jsx
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ExcelDownloadButton from '../../components/common/ExcelDownloadButton';
import { getBranchListExportColumns } from '../../utils/listExportColumns';
import API from '../../api';
import UserLink from '../../components/common/UserLink';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import { formatBranchMode } from '../../config/branchModes';
import UserStatusBadge from '../../components/common/UserStatusBadge';
import ListPageLayout, {
    ListPageHeader,
    ListFilterTabs,
    ListSearchCard,
    ListLoadingSpinner,
    ListEmptyState,
    ListTableCard,
} from '../../components/layout/ListPageLayout';
import PaginationFooter from '../../components/common/PaginationFooter';
import SortableTableHeader from '../../components/common/SortableTableHeader';
import useSortedClientPagination from '../../hooks/useSortedClientPagination';
import { branchListSortColumns } from '../../config/tableSortColumns';
import { getApiErrorInfo } from '../../utils/apiError';
import { showConfirm } from '../../utils/popup';

import { archiveBranch, activateBranch } from '../../api/branches';

// simple debounce (matches pattern used elsewhere)
const useDebounced = (value, delay = 200) => {
    const [v, setV] = useState(value);
    useEffect(() => { const t = setTimeout(() => setV(value), delay); return () => clearTimeout(t); }, [value, delay]);
    return v;
};

const BranchList = () => {
    const { t } = useTranslation();
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [filter, setFilter] = useState('active'); // 'all' | 'active' | 'archived'
    const navigate = useNavigate();

    // search state (full-length search bar)
    const [q, setQ] = useState('');
    const dq = useDebounced(q, 200);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setErr(null);
        try {
            const res = await API.get('/admin/branches');
            setBranches(Array.isArray(res.data) ? res.data : []);
        } catch (e) {
            console.error('Error fetching branches:', e);
            setErr(t('pages.branches.loadFailed'));
            setBranches([]);
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const onEdit = (id) => navigate(`/admin/branches/${id}`);

    const onArchive = async (id) => {
        if (!await showConfirm(t('pages.branches.archiveConfirm'))) return;
        try {
            await archiveBranch(id);
            await fetchData();
        } catch (e) {
            console.error('Archive failed:', e);
            alert(t('pages.branches.archiveFailed'));
        }
    };

    const onActivate = async (id) => {
        try {
            await activateBranch(id);
            await fetchData();
        } catch (e) {
            console.error('Activate failed:', e);
            const errorInfo = getApiErrorInfo(e, t('pages.branches.activateFailed'));
            alert(`${errorInfo.title}\n\n${errorInfo.message}`);
        }
    };

    const renderManager = (b) => {
        const id = b.managerId ?? null;
        const name = (b.managerName || '').trim();
        if (id != null) {
            return <UserLink id={id} name={name || t('pages.branches.managerFallback')} />;
        }
        return '—';
    };

    // apply filter tabs and search
    const filteredBranches = (() => {
        let result = branches;

        // Apply filter tabs
        if (filter === 'active') {
            result = result.filter(b => b.active === true);
        } else if (filter === 'archived') {
            result = result.filter(b => b.active === false);
        }
        // 'all' shows everything

        // Apply search query
        const term = (dq || '').trim().toLowerCase();
        if (term) {
            const pick = (x) => String(x ?? '').toLowerCase();
            result = result.filter((b) => {
                const hay = [
                    b.name,
                    b.branchCode,
                    b.phoneNumber,
                    b.mode,
                    b.managerName,
                    b.managerId,
                    b.id
                ].map(pick).join(' ');
                return term.split(/\s+/).every(t => hay.includes(t));
            });
        }

        return result;
    })();

    const pagination = useSortedClientPagination(filteredBranches, {
        columns: branchListSortColumns,
        resetKey: `${filter}-${dq}`,
    });

    const branchFilterTabs = [
        { key: 'all', label: t('common.all'), count: branches.length },
        { key: 'active', label: t('common.active'), count: branches.filter(b => b.active).length },
        { key: 'archived', label: t('common.archived'), count: branches.filter(b => !b.active).length },
    ];
    const branchTotal = filter === 'all' ? branches.length : filter === 'active' ? branches.filter(b => b.active).length : branches.filter(b => !b.active).length;

    return (
        <ListPageLayout>
            <ListPageHeader
                title={t('pages.branches.title')}
                subtitle={t('pages.branches.subtitle')}
                actions={(
                    <>
                        <ExcelDownloadButton
                            columns={getBranchListExportColumns(t)}
                            data={filteredBranches}
                            filename="branches"
                            disabled={filteredBranches.length === 0}
                        />
                        <button
                            onClick={() => navigate('/admin/branches/new')}
                            className="btn-page-action-primary"
                        >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        {t('pages.branches.add')}
                    </button>
                    </>
                )}
            />

            <ListFilterTabs tabs={branchFilterTabs} active={filter} onChange={setFilter} />

            <ListSearchCard
                value={q}
                onChange={setQ}
                placeholder={t('pages.branches.searchPlaceholder')}
                shown={filteredBranches.length}
                total={branchTotal}
                entityLabel={t('pages.branches.entityLabel')}
            />

            {loading ? (
                <ListLoadingSpinner />
            ) : err ? (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{err}</div>
            ) : filteredBranches.length === 0 ? (
                <ListEmptyState
                    message={q ? t('common.noMatchSearch', { entity: t('pages.branches.entityLabel') }) : t('common.noFound', { entity: t('pages.branches.entityLabel') }) }
                    hint={q ? t('common.emptySearchHint') : undefined}
                />
            ) : (
                <ListTableCard>
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                            <thead className="table-header">
                                <tr>
                                    <SortableTableHeader label={t('pages.branches.colBranchName')} columnKey="name" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('pages.branches.colCode')} columnKey="code" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('common.phone')} columnKey="phone" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('pages.branches.colMode')} columnKey="mode" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('pages.branches.colManagedBy')} columnKey="manager" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('common.status')} columnKey="status" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <th className="table-header-cell">{t('common.actions')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {pagination.pageItems.map((b) => (
                                    <tr key={b.id} className="table-row">
                                        <td className="table-cell">{b.name || <span className="text-gray-400">—</span>}</td>
                                        <td className="table-cell">{b.branchCode || <span className="text-gray-400">—</span>}</td>
                                        <td className="table-cell">{b.phoneNumber || <span className="text-gray-400">—</span>}</td>
                                        <td className="table-cell">
                                            <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                                                {formatBranchMode(b.mode)}
                                            </span>
                                        </td>
                                        <td className="table-cell">{renderManager(b)}</td>
                                        <td className="table-cell">
                                            <UserStatusBadge active={b.active} />
                                        </td>
                                        <td className="table-cell whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => onEdit(b.id)}
                                                    className="btn-action-edit"
                                                    title={t('pages.branches.editTitle')}
                                                >
                                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                    </svg>
                                                    {t('common.edit')}
                                                </button>

                                                {b.active ? (
                                                    <button
                                                        onClick={() => onArchive(b.id)}
                                                        className="btn-action-archive"
                                                        title={t('pages.branches.archiveTitle')}
                                                    >
                                                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                                                        </svg>
                                                        {t('common.archive')}
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => onActivate(b.id)}
                                                        className="btn-action-activate"
                                                        title={t('pages.branches.activateTitle')}
                                                    >
                                                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                        {t('common.activate')}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {pagination.total > 0 && (
                        <PaginationFooter
                            page={pagination.page}
                            size={pagination.size}
                            total={pagination.total}
                            onPageChange={pagination.setPage}
                            onSizeChange={pagination.setSize}
                        />
                    )}
                </ListTableCard>
            )}
        </ListPageLayout>
    );
};

export default BranchList;
